<template>
  <div>
    <!-- 隐藏上传组件 -->
    <el-upload
      v-if="type === 'url'"
      ref="uploadRef"
      :action="uploadUrl"
      :headers="headers"
      :data="params"
      :show-file-list="false"
      :before-upload="beforeUpload"
      :on-success="uploadSuccess"
      :on-error="uploadError"
      style="display: none"
    />

    <!-- 编辑器 -->
    <div ref="editorRef" class="editor" :style="editorStyle"></div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { SYS_BASE_URL, PICTURE_URL } from "@/constant/Const"
import { ElMessage } from 'element-plus'
import Quill from 'quill'
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'

/* ================= props / emits ================= */

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  height: Number,
  minHeight: Number,
  readOnly: {
    type: Boolean,
    default: false
  },
  type: {
    type: String,
    default: 'url' // url | base64
  },
  fileSize: {
    type: Number,
    default: 5
  },
  params: {
    type: Object,
    default: () => ({})
  },
  name: {
    type: String,
    default: 'file'
  }
})

const emit = defineEmits(['update:modelValue', 'change'])

/* ================= refs ================= */

const editorRef = ref(null)
const uploadRef = ref(null)
let quill = null

/* ================= config ================= */

const uploadUrl = SYS_BASE_URL + '/api/img/uploadSign'
const headers = {
  Authorization: sessionStorage.getItem('token')
}

const options = {
  theme: 'snow',
  readOnly: props.readOnly,
  modules: {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ size: ['small', false, 'large', 'huge'] }],
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ color: [] }, { background: [] }],
      [{ align: [] }],
      ['clean'],
      ['link', 'image', 'video']
    ]
  },
  placeholder: '请输入内容'
}

/* ================= computed ================= */

const editorStyle = computed(() => {
  const style = {}
  if (props.minHeight) style.minHeight = props.minHeight + 'px'
  if (props.height) style.height = props.height + 'px'
  return style
})

/* ================= lifecycle ================= */

onMounted(() => {
  initEditor()
})

onBeforeUnmount(() => {
  quill = null
})

/* ================= watch ================= */

watch(
  () => props.modelValue,
  (val) => {
    if (quill && val !== quill.root.innerHTML) {
      quill.root.innerHTML = val || ''
    }
  }
)

/* ================= methods ================= */

function initEditor() {
  quill = new Quill(editorRef.value, options)

  // 初始值
  quill.root.innerHTML = props.modelValue || ''

  // v-model
  quill.on('text-change', () => {
    const html = quill.root.innerHTML
    emit('update:modelValue', html)
    emit('change', html)
  })

  // 自定义图片上传
  if (props.type === 'url') {
    const toolbar = quill.getModule('toolbar')
    toolbar.addHandler('image', () => {
      quill.focus()
      uploadRef.value?.$el.querySelector('input').click()
    })
  }
}

/* ================= upload ================= */

function beforeUpload(file) {
  const isImg = ['image/jpeg', 'image/png', 'image/jpg', 'image/svg+xml'].includes(
    file.type
  )
  if (!isImg) {
    ElMessage.error('图片格式错误')
    return false
  }

  const isLt = file.size / 1024 / 1024 < props.fileSize
  if (!isLt) {
    ElMessage.error(`图片不能超过 ${props.fileSize}MB`)
    return false
  }

  return true
}

function uploadSuccess(res) {
  if (!quill) return

  let index = quill.getSelection()?.index
  if (index == null) index = quill.getLength()

  const url =
    'https://' +
    res.data.domain +
    '/' +
    res.data.pathPrefix +
    res.data.storageFilePath +
    res.data.newFileName

  quill.insertEmbed(index, 'image', url)
  quill.setSelection(index + 1)
}

function uploadError() {
  ElMessage.error('图片上传失败')
}
</script>

<style scoped>
.editor {
  line-height: normal;
  white-space: pre-wrap;
}
</style>
